import React, { createContext, useState, useContext, useEffect } from 'react';
import { canRole } from '../utils/permissions';
import { login as apiLogin, register as apiRegister, getCurrentUser } from '../services/authService';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    
    if (storedToken) {
      // Если есть токен, загружаем пользователя
      loadUser();
      
      // Если не выбран "Запомнить меня", устанавливаем таймер на выход
      if (!rememberMe) {
        // Токен будет жить до закрытия браузера
        console.log('Session will end when browser closes');
      }
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const response = await getCurrentUser();
      setUser(response.user);
    } catch (error) {
      console.error('Failed to load user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      console.log('AuthContext: login called with:', email);
      const response = await apiLogin(email, password);
      console.log('AuthContext: API response received:', response);
      
      if (!response.token) {
        console.error('AuthContext: No token in response!');
        throw new Error('No token received');
      }
      
      // Сохраняем токен
      localStorage.setItem('token', response.token);
      
      // Очищаем кэшированные данные прогресса при входе
		localStorage.removeItem('lessonProgressCache');
		sessionStorage.removeItem('lessonProgressCache');
		// Если есть другие кэшированные данные ученика
		localStorage.removeItem('cachedProgress');
		sessionStorage.removeItem('cachedLessonData');
      
      // Если выбран "Запомнить меня", устанавливаем флаг
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberMe');
      }
      
      console.log('AuthContext: Token saved to localStorage');
      
      setToken(response.token);
      console.log('AuthContext: Token state updated');
      
      setUser(response.user);
      console.log('AuthContext: User state updated:', response.user);
      
      toast.success('Вход выполнен успешно!');
      console.log('AuthContext: Toast shown');
      
      return response;
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      toast.error(error.response?.data?.message || 'Ошибка входа');
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await apiRegister(userData);
      localStorage.setItem('token', response.token);
      setToken(response.token);
      setUser(response.user);
      toast.success('Регистрация успешна!');
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ошибка регистрации');
      throw error;
    }
  };

  // Применить сессию из ответа (после подтверждения email) — логиним пользователя.
  const applySession = (response) => {
    if (!response?.token) return;
    localStorage.setItem('token', response.token);
    localStorage.removeItem('lessonProgressCache');
    setToken(response.token);
    setUser(response.user);
  };

  // Точечно обновить данные текущего пользователя в состоянии
  // (после редактирования профиля), чтобы UI обновился сразу, без перезагрузки.
  const updateUser = (partial) => {
    setUser(prev => prev ? { ...prev, ...partial } : prev);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('rememberedEmail');
    
    // 🔥 ОЧИСТКА КЭША ПРОГРЕССА 🔥
	  localStorage.removeItem('lessonProgressCache');
	  sessionStorage.removeItem('lessonProgressCache');
	  localStorage.removeItem('cachedProgress');
	  sessionStorage.removeItem('cachedLessonData');
    setToken(null);
    setUser(null);
    toast.success('Выход выполнен');
  };

  const value = {
    user,
    loading,
    login,
    register,
    applySession,
    logout,
    updateUser,
    // может ли текущий сотрудник выполнить действие (см. utils/permissions.js)
    can: (perm) => canRole(user?.role, perm),
    role: user?.role || null,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};